import React, { useState, useEffect, useMemo } from "react";
import {
  apiService,
  API_CONFIG,
  WinPrize,
  GetWinPrizesResponse,
  Campaign,
  GetCampaignsResponse,
} from "../../services";
import { authService } from "../../services/auth";
import VoucherExchangeTab from "./VoucherExchangeTab";

// Component để hiển thị nội dung có thể thu gọn
const ExpandableContent: React.FC<{
  content: string;
  maxLines?: number;
  className?: string;
}> = ({ content, maxLines = 2, className = "" }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldShowToggle, setShouldShowToggle] = useState(false);

  useEffect(() => {
    // Kiểm tra xem nội dung có vượt quá số dòng cho phép không
    const element = document.createElement("div");
    element.style.cssText = `
      position: absolute;
      visibility: hidden;
      white-space: pre-wrap;
      word-wrap: break-word;
      max-width: 200px;
      line-height: 1.4;
      font-size: 14px;
    `;
    element.textContent = content;
    document.body.appendChild(element);

    const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
    const maxHeight = lineHeight * maxLines;
    const shouldShow = element.offsetHeight > maxHeight;

    document.body.removeChild(element);
    setShouldShowToggle(shouldShow);
  }, [content, maxLines]);

  if (!content || content === "-") {
    return <span className={className}>-</span>;
  }

  return (
    <div className="relative">
      <div
        className={`${className} ${!isExpanded ? "line-clamp-2" : ""}`}
        style={{
          display: "-webkit-box",
          WebkitLineClamp: !isExpanded ? maxLines : undefined,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
        }}
      >
        {content}
      </div>
      {shouldShowToggle && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-xs mt-1 block"
        >
          {isExpanded ? "Thu gọn" : "Xem thêm"}
        </button>
      )}
    </div>
  );
};

interface Redemption {
  id: string;
  date: string;
  fullName: string;
  phone: string;
  codes: string[];
  campaign: string;
  status: "completed" | "pending" | "cancelled";
  totalValue: number;
  winCount: number; // Số lần trúng
  address?: string; // Địa chỉ
  ipLocal?: string; // IP local
}

interface CampaignStats {
  id: string;
  name: string;
  totalRewards: number;
  exchangedRewards: number;
  remainingRewards: number;
}

const RedemptionManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"gift" | "voucher">("gift");
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(false); // Changed to false - no initial loading
  const [error, setError] = useState<string | null>(null);

  // Check user role
  const isAdmin = authService.isAdmin();

  // Auto hide error notification after 3 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 2000);
    return () => clearTimeout(timer);
  }, [error]);

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false); // Changed to false - no initial loading

  const [selectedCampaign, setSelectedCampaign] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("");
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    name: "",
    status: "all",
  });

  // Modal state for showing all codes
  const [showCodesModal, setShowCodesModal] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [selectedPerson, setSelectedPerson] = useState("");

  // Transform API data to component format
  const transformWinPrizeToRedemption = (
    winPrize: WinPrize,
    campaignsList: Campaign[]
  ): Redemption => {
    // Find campaign name by ID
    console.log("Transform - winPrize.campaign_id:", winPrize.campaign_id);
    console.log(
      "Transform - campaignsList:",
      campaignsList.map((c) => ({ id: c._id, name: c.name }))
    );

    const campaign = campaignsList.find(
      (c) => c._id && c._id === winPrize.campaign_id
    );
    console.log("Transform - found campaign:", campaign);

    const campaignName = campaign
      ? campaign.name
      : "Chiến dịch không còn tồn tại";
    console.log("Transform - campaignName:", campaignName);
    const winAny = winPrize as unknown as {
      code?: string;
      codes?: string[];
      num_win?: number;
    };
    const codeList = Array.isArray(winAny.codes)
      ? winAny.codes
      : typeof winAny.code === "string"
      ? winAny.code
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : [];

    return {
      id: winPrize._id,
      date: winPrize.winning_day,
      fullName: winPrize.prize_winner,
      phone: winPrize.phone,
      codes: codeList,
      campaign: campaignName,
      status: "completed", // Assuming all win prizes are completed
      totalValue: 0, // API doesn't provide value
      winCount:
        typeof winAny.num_win === "number"
          ? winAny.num_win
          : codeList.length || 1,
      address: winPrize.address,
      ipLocal: winPrize.ip_local,
    };
  };

  // Fetch campaigns data
  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const campaignsResponse = await apiService.getCampaigns();

        if (campaignsResponse.success && campaignsResponse.data) {
          setCampaigns(campaignsResponse.data);

          // Tự động chọn chiến dịch duy nhất nếu chỉ có 1 chiến dịch
          if (campaignsResponse.data.length === 1) {
            setSelectedCampaign(campaignsResponse.data[0].name);
          }
        }
      } catch (err) {
        console.error("Error fetching campaigns:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Đã xảy ra lỗi khi tải dữ liệu chiến dịch"
        );
      }
    };

    fetchCampaigns();
  }, []);

  // Fetch win prizes data with search and filters
  useEffect(() => {
    const fetchWinPrizes = async () => {
      try {
        setLoading(true);

        // Reset pagination when search changes
        setCurrentPage(1);

        // Determine campaign ID for API call
        const campaignId =
          selectedCampaign === "all"
            ? undefined
            : campaigns.find((c) => c.name === selectedCampaign)?._id;

        // Call API with search parameter
        const searchTerm = filters.name.trim() || undefined;
        console.log("API Call Debug:", {
          campaignId,
          sortBy,
          searchTerm: filters.name,
          searchTermTrimmed: searchTerm,
          isSearchEmpty: !searchTerm,
          fullUrl: `https://mhd.api.vm.dfm-europe.com/api/win-prizes?${new URLSearchParams(
            {
              ...(campaignId && { campaign_id: campaignId }),
              ...(sortBy && { sort: sortBy }),
              ...(searchTerm && { search: searchTerm }),
            }
          ).toString()}`,
        });

        const winPrizesResponse = await apiService.getWinPrizes(
          campaignId,
          sortBy,
          searchTerm
        );

        if (winPrizesResponse.success && winPrizesResponse.data) {
          const transformedRedemptions = winPrizesResponse.data.map(
            (winPrize) => transformWinPrizeToRedemption(winPrize, campaigns)
          );
          console.log("API Search Results:", {
            searchTerm: filters.name,
            apiData: winPrizesResponse.data,
            transformedData: transformedRedemptions,
          });
          setRedemptions(transformedRedemptions);
        } else {
          setError("Không thể tải dữ liệu đổi quà");
        }
      } catch (err) {
        console.error("Error fetching win prizes:", err);
        setError(
          err instanceof Error ? err.message : "Đã xảy ra lỗi khi tải dữ liệu"
        );
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if campaigns are loaded
    if (campaigns.length > 0) {
      fetchWinPrizes();
    }
  }, [selectedCampaign, sortBy, filters.name, campaigns]);

  // No need for client-side campaign filtering since API handles it
  const filteredRedemptionsByCampaign = redemptions;

  // Handle campaign selection change - auto clear filters when switching campaigns
  const handleCampaignChange = (newCampaign: string) => {
    setSelectedCampaign(newCampaign);

    // Always clear other filters when switching campaigns to ensure data is visible
    setFilters({
      startDate: "",
      endDate: "",
      name: "",
      status: "all",
    });
  };

  // Check if any filters are active (excluding campaign selection)
  const hasActiveFilters =
    filters.startDate ||
    filters.endDate ||
    filters.name ||
    filters.status !== "all";

  // Get campaigns that have redemptions data for better UX
  const campaignsWithRedemptions = useMemo(() => {
    const campaignsWithData = new Set(redemptions.map((r) => r.campaign));
    return campaigns.filter((c) => campaignsWithData.has(c.name));
  }, [campaigns, redemptions]);

  // Get all campaigns (including those without redemptions) for dropdown
  const allCampaignsForDropdown = campaigns;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "Hoàn thành";
      case "pending":
        return "Đang xử lý";
      case "cancelled":
        return "Đã hủy";
      default:
        return "Không xác định";
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  // Apply date filtering only (search is now handled by API)
  const filteredRedemptions = filteredRedemptionsByCampaign.filter(
    (redemption) => {
      // Lọc theo khoảng thời gian (vẫn giữ client-side filtering cho date)
      const redemptionDate = new Date(redemption.date);
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      let matchesTime = true;
      if (startDate && endDate) {
        matchesTime = redemptionDate >= startDate && redemptionDate <= endDate;
      } else if (startDate) {
        matchesTime = redemptionDate >= startDate;
      } else if (endDate) {
        matchesTime = redemptionDate <= endDate;
      }

      return matchesTime;
    }
  );

  // Tính toán thống kê cho chiến dịch được chọn
  const getSelectedCampaignStats = () => {
    if (selectedCampaign === "all") {
      // Calculate stats for all campaigns
      const totalRewards = campaigns.reduce(
        (sum, c) => sum + (c.total_gift || 0),
        0
      );
      const exchangedRewards = filteredRedemptionsByCampaign.length; // Count of redemptions for all campaigns
      const remainingRewards = totalRewards - exchangedRewards;

      return {
        totalRewards,
        exchangedRewards,
        remainingRewards: Math.max(0, remainingRewards),
      };
    }

    // Calculate stats for selected campaign
    const campaign = campaigns.find((c) => c.name === selectedCampaign);
    if (!campaign) {
      return { totalRewards: 0, exchangedRewards: 0, remainingRewards: 0 };
    }

    // Count redemptions for this specific campaign
    const exchangedRewards = filteredRedemptionsByCampaign.length;
    const totalRewards = campaign.total_gift || 0;
    const remainingRewards = Math.max(0, totalRewards - exchangedRewards);

    return {
      totalRewards,
      exchangedRewards,
      remainingRewards,
    };
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);

      // Get campaign_id if a specific campaign is selected
      let campaignId: string | undefined;
      if (selectedCampaign !== "all") {
        const campaign = campaigns.find((c) => c.name === selectedCampaign);
        campaignId = campaign?._id;
      }

      const { blob } = await apiService.exportWinPrizes(campaignId);

      // Create custom filename based on campaign name
      const dateStr = new Date().toISOString().split("T")[0];
      let customFilename: string;

      if (selectedCampaign === "all") {
        customFilename = `win-prizes-all-campaigns-${dateStr}.xlsx`;
      } else {
        // Clean campaign name for filename (remove special characters)
        const cleanCampaignName = selectedCampaign
          .replace(/[^a-zA-Z0-9\s]/g, "") // Remove special characters
          .replace(/\s+/g, "-") // Replace spaces with hyphens
          .toLowerCase();
        customFilename = `win-prizes-${cleanCampaignName}-${dateStr}.xlsx`;
      }

      // Create download link
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.download = customFilename; // Use custom filename

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed:", error);
      setError(
        error instanceof Error ? error.message : "Lỗi khi xuất file Excel"
      );
    } finally {
      setLoading(false);
    }
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Calculate pagination
  const totalPages = Math.ceil(filteredRedemptions.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRedemptions = filteredRedemptions.slice(startIndex, endIndex);

  // Debug log for table display
  console.log("Table Display Debug:", {
    redemptions: redemptions.length,
    filteredRedemptions: filteredRedemptions.length,
    currentRedemptions: currentRedemptions.length,
    searchTerm: filters.name,
    currentPage,
    rowsPerPage,
    startIndex,
    endIndex,
    totalPages,
    paginationRange: `Hiển thị ${startIndex + 1}-${Math.min(
      endIndex,
      filteredRedemptions.length
    )} trong ${filteredRedemptions.length} kết quả`,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  const handleShowAllCodes = (codes: string[], personName: string) => {
    setSelectedCodes(codes);
    setSelectedPerson(personName);
    setShowCodesModal(true);
  };

  const campaignStats = getSelectedCampaignStats();

  // No more full-screen loading - we'll show content immediately with loading indicators

  return (
    <div>
      {/* Error notification */}
      {error && (
        <div className="fixed top-4 right-4 z-50">
          <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-lg max-w-sm">
            <div className="flex items-center">
              <div className="text-white mr-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h4 className="text-sm font-medium">
                  Không tìm thấy file dữ liệu
                </h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Quản lý Đổi quà
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Theo dõi đổi quà và đổi voucher theo chiến dịch
          </p>
        </div>
        {activeTab === "gift" && isAdmin && (
          <button
            onClick={handleExportExcel}
            disabled={loading}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 dark:hover:bg-green-500 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>{loading ? "Đang xuất..." : "Xuất File Excel"}</span>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6">
          <button
            type="button"
            onClick={() => setActiveTab("gift")}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "gift"
                ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            Đổi quà
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("voucher")}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "voucher"
                ? "border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            Đổi voucher
          </button>
        </nav>
      </div>

      {activeTab === "voucher" ? (
        <VoucherExchangeTab />
      ) : (
        <>
      {/* Search Bar */}
      <div className="mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1 max-w-2xl relative">
            <input
              type="text"
              placeholder="Tìm kiếm theo tên hoặc SĐT..."
              value={filters.name}
              onChange={(e) => {
                const value = e.target.value;
                setFilters((prev) => ({ ...prev, name: value }));
              }}
              className="w-full px-4 py-3 pl-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className=" rounded-lg p-4 shadow-sm">
          <div className="flex items-end flex-wrap gap-4 justify-between">
            <div className="flex items-end gap-3">
              <div className="flex flex-col">
                <label className="mb-1 text-sm text-gray-600 dark:text-gray-300">Từ ngày</label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => {
                    setFilters((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }));
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-1 text-sm text-gray-600 dark:text-gray-300">Đến ngày</label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => {
                    setFilters((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }));
                  }}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
                />
              </div>

              {/* Sort by win count */}
              <div className="flex flex-col">
                <label className="mb-1 text-sm text-gray-600 dark:text-gray-300">
                  Sắp xếp theo số lần trúng
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px]"
                >
                  <option value="">--</option>
                  <option value="asc">Tăng dần</option>
                  <option value="desc">Giảm dần</option>
                </select>
              </div>

              {/* Clear Filter Button - Only show when filters are active (excluding campaign selection) */}
              {hasActiveFilters && (
                <button
                  onClick={() => {
                    setFilters({
                      startDate: "",
                      endDate: "",
                      name: "",
                      status: "all",
                    });
                    // Don't reset campaign selection - keep the selected campaign
                  }}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Xóa lọc
                </button>
              )}
            </div>
            <div className="ml-auto flex flex-col items-end space-y-1">
              {campaigns.length === 0 ? (
                // Loading state for campaigns
                <div className="flex items-center space-x-2 px-3 py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Đang tải chiến dịch...
                  </span>
                </div>
              ) : campaigns.length === 1 ? (
                // Hiển thị tên chiến dịch khi chỉ có 1 chiến dịch
                <div className="flex items-center space-x-2 px-3 py-2">
                  <label className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    Chiến dịch:
                  </label>
                  <div className="px-3 py-2 text-sm border-blue-200 dark:border-blue-800 rounded-lg text-blue-800 dark:text-blue-300 font-medium bg-blue-50 dark:bg-blue-900/30">
                    {campaigns[0].name}
                  </div>
                </div>
              ) : (
                // Hiển thị dropdown khi có nhiều chiến dịch
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                    Chọn chiến dịch:
                  </label>
                  <div className="relative">
                    <select
                      value={selectedCampaign}
                      onChange={(e) => handleCampaignChange(e.target.value)}
                      className="appearance-none pr-8 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[220px]"
                      disabled={campaignsLoading}
                      aria-label="Chọn chiến dịch"
                    >
                      <option value="all">Tất cả chiến dịch</option>
                      {allCampaignsForDropdown.map((campaign) => {
                        const hasRedemptions = redemptions.some(
                          (r) => r.campaign === campaign.name
                        );
                        return (
                          <option key={campaign._id} value={campaign.name}>
                            {campaign.name}{" "}
                            {!hasRedemptions ? "(Không có dữ liệu)" : ""}
                          </option>
                        );
                      })}
                    </select>
                    <svg
                      className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Show message when selected campaign has no data */}
      {selectedCampaign !== "all" &&
        filteredRedemptionsByCampaign.length === 0 && (
          <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <p className="text-yellow-800 dark:text-yellow-300 text-sm">
                Chiến dịch "{selectedCampaign}" hiện tại không có dữ liệu đổi
                quà. Bạn có thể chọn chiến dịch khác hoặc chọn "Tất cả chiến
                dịch" để xem tất cả dữ liệu.
              </p>
            </div>
          </div>
        )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Tổng số lượng phần thưởng
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {campaignStats.totalRewards.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Số lượng đã đổi
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {campaignStats.exchangedRewards.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 px-4 py-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <svg
                className="w-6 h-6 text-orange-600 dark:text-orange-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                Số lượng còn lại
              </p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {campaignStats.remainingRewards.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Redemptions Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  IP
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Ngày
                </th>
                {selectedCampaign === "all" && (
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                    Tên chiến dịch
                  </th>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Họ tên
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  SĐT
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Địa chỉ
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  Các mã
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                  số lần trúng
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                // Loading state for table
                <tr>
                  <td
                    colSpan={selectedCampaign === "all" ? 8 : 7}
                    className="px-4 py-12 text-center"
                  >
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                      <p className="text-gray-600 dark:text-gray-300">Đang tải dữ liệu...</p>
                    </div>
                  </td>
                </tr>
              ) : currentRedemptions.length > 0 ? (
                currentRedemptions.map((redemption) => (
                  <tr
                    key={redemption.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <td className="px-4 py-4">
                      <ExpandableContent
                        content={redemption.ipLocal || "-"}
                        className="text-sm text-gray-900 dark:text-white font-mono"
                        maxLines={2}
                      />
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {new Date(redemption.date).toLocaleDateString("vi-VN")}
                      </div>
                    </td>
                    {selectedCampaign === "all" && (
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                          {redemption.campaign}
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {redemption.fullName}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {redemption.phone}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <ExpandableContent
                        content={redemption.address || "-"}
                        className="text-sm text-gray-900 dark:text-white"
                        maxLines={2}
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        {redemption.codes.length > 0 ? (
                          <>
                            {redemption.codes.slice(0, 3).map((code, index) => (
                              <div
                                key={index}
                                className="inline-flex items-center bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-1.5 shadow-sm"
                              >
                                <span className="font-mono text-sm text-blue-800 dark:text-blue-300">
                                  {code}
                                </span>
                              </div>
                            ))}
                            {redemption.codes.length > 3 && (
                              <button
                                onClick={() =>
                                  handleShowAllCodes(
                                    redemption.codes,
                                    redemption.fullName
                                  )
                                }
                                className="inline-flex items-center text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                              >
                                +{redemption.codes.length - 3} vé
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-500 dark:text-gray-400 text-sm">
                            Không có mã
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Trúng lần {redemption.winCount}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={selectedCampaign === "all" ? 8 : 7}
                    className="px-4 py-12 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="flex flex-col items-center">
                      <svg
                        className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <p className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">
                        Không có dữ liệu đổi quà nào
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        Hãy thử thay đổi bộ lọc hoặc chọn chiến dịch khác
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {filteredRedemptions.length > 0 && (
          <div className="bg-white dark:bg-gray-800 px-4 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Hiển thị</span>
              <select
                value={rowsPerPage}
                onChange={(e) =>
                  handleRowsPerPageChange(Number(e.target.value))
                }
                className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={5}>5 dòng</option>
                <option value={10}>10 dòng</option>
                <option value={25}>25 dòng</option>
                <option value={50}>50 dòng</option>
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Hiển thị {startIndex + 1}-
                {Math.min(endIndex, filteredRedemptions.length)} trong{" "}
                {filteredRedemptions.length} kết quả
              </span>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &lt;
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 text-sm border rounded transition-colors ${
                        currentPage === pageNum
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal for showing all codes */}
      {showCodesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-4 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Tất cả mã của {selectedPerson}
              </h3>
              <button
                onClick={() => setShowCodesModal(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {selectedCodes.map((code, index) => (
                  <div
                    key={index}
                    className="flex items-center p-3 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800 hover:from-blue-100 hover:to-blue-200 dark:hover:from-blue-900/50 dark:hover:to-blue-900/50 transition-colors shadow-sm"
                  >
                    <span className="font-mono text-sm text-blue-900 dark:text-blue-300 truncate">
                      {code}
                    </span>
                    <span className="text-xs text-blue-700 dark:text-blue-400 ml-auto pl-2">
                      #{index + 1}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end flex-shrink-0">
              <button
                onClick={() => setShowCodesModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default RedemptionManagement;
